import { useQuery } from '@tanstack/react-query';
import type { LiveContextFocusMode } from '@/enums/live-context-focus-mode.enum';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import { useAuth } from '@clerk/clerk-react';
import type {
  FlowCueStateV1,
  LiveCueSheetV1,
  MediaAgentRecoveryV1,
  PerformControlV1,
} from '@/types/player/perform-control-v1.types.ts';
import type { ResolvedMediaControlSettings } from '@/types/playout/media-control-settings.types';

export const GET_LIVE_CONTEXT_STATE_QUERY_KEY = 'get-live-context-state';

export interface LiveContextStateDto {
  /** On Air camera focus for template selection. */
  focusMode?: LiveContextFocusMode;
  /** The current section's remote speakers are in the program. A section change turns it off. */
  remoteSpeakersOnStage?: boolean;
  stagedSelection?: {
    selectedItemIds: string[];
    /** Backend returns 'both'; legacy used 'mixedView'. Both map to LiveContextShowMode.BOTH */
    showMode: 'mediaOnly' | 'both' | 'mixedView' | 'speakerOnly';
    focusedItemId?: string;
    skippedItemIds?: string[];
    /** Latched Next item id for operator look-ahead showMode overrides. */
    lookAheadForItemId?: string;
  };
  playState?: {
    layers?: Array<{
      layerId: number;
      componentId: string;
      position: { x: number; y: number; w: number; h: number };
      cutLeft?: number | null;
      cutRight?: number | null;
      data: Record<string, string>;
      visible?: boolean;
      preload?: boolean;
      audible?: boolean;
      trackSid?: string;
      trackSids?: string[];
      itemId?: string;
    }>;
    preloadId?: string;
    playedItemIds?: Array<{ itemId: string; layerId: number; preload: boolean; preloadConfirmed: boolean }>;
    preloadedItemIds?: Array<{ itemId: string; layerId: number; preload: boolean; preloadConfirmed: boolean }>;
    showMode?: string;
    byPlayer?: Record<
      string,
      {
        layers: Array<{
          layerId: number;
          componentId: string;
          position: { x: number; y: number; w: number; h: number };
          cutLeft?: number | null;
          cutRight?: number | null;
          data: Record<string, string>;
          visible?: boolean;
          preload: boolean;
          preloadConfirmed?: boolean;
          audible?: boolean;
          trackSid?: string;
          trackSids?: string[];
          itemId?: string;
        }>;
      }
    >;
    itemAudibleOverrides?: Record<string, boolean>;
    /** Stage/remote mic override metadata (segment vs permanent). */
    micAudibleControl?: {
      overrides: Record<string, { audible: boolean; mode: 'segment' | 'permanent' }>;
      lastSegmentDefaults: Record<string, boolean>;
    };
    /** Agent-backed presentation deck currently carried on the persistent VM video track. */
    activeAgentDeckMediaId?: string;
    /** Identity for Control Next `_lookahead:*` layers. */
    lookAheadPreview?: {
      itemId: string;
      showMode?: string;
      scope?: string;
      slotId?: string;
    };
    /** Sequential Next+1 under `_lookahead2:*`. */
    lookAheadPreview2?: {
      itemId: string;
      showMode?: string;
      scope?: string;
      slotId?: string;
    };
    /** Insert resume overlay under `_lookaheadResume:*`. */
    lookAheadInsertResume?: {
      itemId: string;
      showMode?: string;
      scope?: string;
      slotId?: string;
    };
    /** Sequential -1 under `_lookaheadPrev:*` (operator-only, not a third pane). */
    lookAheadPrevious?: {
      itemId: string;
      showMode?: string;
      scope?: string;
      slotId?: string;
    };
    /** True while a media-agent deck is opening (not preloaded) — hide agent LiveKit like players. */
    presentationTransitionInFlight?: boolean;
    /** PPT pair on imageSequence stills. Official backup switch. */
    backupSlides?: boolean;
    /** Process-local live-context-play-state seq at GET time. */
    seq?: number;
  };
  /** Authoritative perform UI (preload / transition / wait); mirror `PerformControlV1` on the service. */
  performControl?: PerformControlV1;
  /** Auto-cue orchestrator state when `perform_auto_cue` feature is enabled. */
  cueState?: FlowCueStateV1;
  /** Operator GET only: projected live cue sheet when a session is running. */
  cueSheet?: LiveCueSheetV1;
  /** Operator GET only: session resume slide/page per bare mediaId. */
  mediaSequenceIndex?: Record<string, number>;
  /** Media-agent outage episode mirror of the `live-context-perform-control` field. */
  mediaAgentRecovery?: MediaAgentRecoveryV1;
  mediaControl?: ResolvedMediaControlSettings;
  liveLanguage?: {
    enabled?: boolean;
    captions?: { stagePlayer?: boolean; roomPlayer?: boolean; onlinePlayer?: boolean };
    showLanguageMarker?: boolean;
    clearEpoch?: number;
    status?: string;
    translationTemplateMissing?: { stagePlayer?: boolean; roomPlayer?: boolean; onlinePlayer?: boolean };
  };
}

export const useGetLiveContextStateQuery = (entityRef: string | undefined, options?: { enabled?: boolean }) => {
  const { getToken } = useAuth();
  const enabled = !!entityRef && (options?.enabled ?? true);

  return useQuery({
    queryKey: [GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef],
    queryFn: async () => {
      const token = await getToken();
      return getLiveContextState({ token: token ?? '', entityRef: entityRef! });
    },
    enabled,
  });
};

const getLiveContextState = async ({
  token,
  entityRef,
}: {
  token: string;
  entityRef: string;
}): Promise<LiveContextStateDto> => {
  const url = `${import.meta.env.VITE_API_URL}/player/live-context-state?entityRef=${encodeURIComponent(entityRef)}`;
  return sendJSONRequest({ url, method: METHOD.GET, token });
};
